import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const RED       = '#DC2626';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

const ROLE_TABLE = { worker: 'workers', employer: 'employers', union: 'union_accounts' };

const DELETE_RPC = {
  worker: 'delete_own_worker_account',
  employer: 'delete_own_employer_account',
  union: 'delete_own_union_account',
};

// `value` is what's actually compared (against 'Other') and written to cancellation_reason --
// kept as the original stable English literal so existing stored data and this comparison logic
// never change, regardless of display language. `labelKey` is resolved separately via t() for
// the radio button text, matching the same value/label split used by regionOf() and
// SCHOOL_TYPE_KEYS elsewhere in the app for this exact "display text doubling as a stored value"
// bug shape.
// delete_own_employer_account() refuses with exactly this message (and code P0001) while the
// employer still has a live Stripe subscription. Both are matched, so any other P0001 raised by
// the delete functions gets the generic copy instead of the billing one.
const BILLING_REFUSAL_MESSAGE = 'Cancel your subscription before deleting your account';
const isBillingRefusal = (error) => error?.code === 'P0001' && (error?.message || '').trim() === BILLING_REFUSAL_MESSAGE;
// Same definition of "live" as the database guard.
const ENDED_SUBSCRIPTION_STATUSES = ['canceled', 'incomplete_expired'];
const isLiveSubscription = (sub) => !!sub?.stripe_subscription_id && !ENDED_SUBSCRIPTION_STATUSES.includes(sub.status || '');

const CANCEL_REASONS = [
  { value: 'Too expensive', labelKey: 'deleteAccountModal.reasonTooExpensive' },
  { value: 'Not finding the right candidates', labelKey: 'deleteAccountModal.reasonNotFindingCandidates' },
  { value: 'Switching to another platform', labelKey: 'deleteAccountModal.reasonSwitchingPlatform' },
  { value: 'No longer hiring', labelKey: 'deleteAccountModal.reasonNoLongerHiring' },
  { value: 'Other', labelKey: 'deleteAccountModal.reasonOther' },
];

// onContactSupport (optional): opens the dashboard's existing support chat. Offered only in the
// billing-blocked state, since cancelling a subscription currently goes through support.
export default function DeleteAccountModal({ supabase, userId, role, onClose, onContactSupport }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isSubscription = role === 'employer' || role === 'union';
  const [step, setStep]             = useState(isSubscription ? 'reason' : 'confirm');
  const [reasonOption, setReasonOption] = useState(null);
  const [reasonDetail, setReasonDetail] = useState('');
  const [option, setOption]         = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [processing, setProcessing] = useState(false);
  const [errorKind, setErrorKind]   = useState(null); // null | 'billing' | 'generic'
  const [finished, setFinished]     = useState(null); // null | 'deactivate' | 'delete'

  // The modal scrolls on short screens, where the message (and the billing state's Contact
  // Support button) would otherwise appear below the fold.
  const errorRef = useRef(null);
  useEffect(() => {
    if (errorKind) errorRef.current?.scrollIntoView({ block: 'nearest' });
  }, [errorKind]);

  const reasonValid = reasonOption && (reasonOption !== 'Other' || reasonDetail.trim().length > 0);
  const canConfirm = (option === 'deactivate' || option === 'delete') && confirmText === 'DELETE' && !processing;

  const chooseOption = (value) => { setOption(value); setErrorKind(null); };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setProcessing(true);
    setErrorKind(null);
    const table = ROLE_TABLE[role];
    const fail = (kind) => { setProcessing(false); setErrorKind(kind); };

    // Friendly pre-check, so a blocked employer is told before anything is written. Covers both
    // options: deactivating would hide the account while Stripe keeps billing it. For Delete
    // Immediately the database guard inside delete_own_employer_account() stays the real
    // authority -- a failed or stale read here just falls through to it, and its refusal is
    // handled below. Deactivate has no database-side billing guard; this check is all there is.
    if (role === 'employer') {
      const { data: subscriptions, error: subscriptionError } = await supabase
        .from('employer_subscriptions')
        .select('stripe_subscription_id, status')
        .eq('employer_id', userId);
      if (!subscriptionError && (subscriptions || []).some(isLiveSubscription)) { fail('billing'); return; }
    }

    // The reason is saved before the account is removed/hidden, so the previous value is kept
    // and written back if the step after it fails -- an account that is still here must not be
    // left carrying a cancellation reason.
    let reasonWritten = false;
    let previousReason = null;
    const restoreReason = async () => {
      if (!reasonWritten) return;
      const { data: restored, error: restoreError } = await supabase
        .from(table).update({ cancellation_reason: previousReason }).eq('id', userId).select('id');
      if (restoreError || restored?.length !== 1) {
        console.error('[DeleteAccountModal] Could not restore the previous cancellation reason:', restoreError?.message || 'no row returned');
      }
    };

    if (isSubscription) {
      const detail = reasonDetail.trim();
      const finalReason = reasonOption === 'Other'
        ? (detail || 'Other')
        : (detail ? `${reasonOption} — ${detail}` : reasonOption);
      const { data: current, error: currentError } = await supabase
        .from(table).select('cancellation_reason').eq('id', userId).maybeSingle();
      if (currentError || !current) { fail('generic'); return; }
      previousReason = current.cancellation_reason ?? null;
      const { data: saved, error: reasonError } = await supabase
        .from(table).update({ cancellation_reason: finalReason }).eq('id', userId).select('id');
      if (reasonError || saved?.length !== 1) { fail('generic'); return; }
      reasonWritten = true;
    }

    if (option === 'deactivate') {
      const { data: deactivated, error: deactivateError } = await supabase
        .from(table).update({ deactivated_at: new Date().toISOString() }).eq('id', userId).select('id');
      if (deactivateError || deactivated?.length !== 1) {
        await restoreReason();
        fail('generic');
        return;
      }
      await supabase.auth.signOut();
      setProcessing(false);
      setFinished('deactivate');
      return;
    }

    const { error: deleteError } = await supabase.rpc(DELETE_RPC[role]);
    if (deleteError) {
      await restoreReason();
      fail(isBillingRefusal(deleteError) ? 'billing' : 'generic');
      return;
    }
    await supabase.auth.signOut();
    setProcessing(false);
    setFinished('delete');
  };

  // Shown in place of the form once the account is gone/hidden and the session is signed out;
  // the only way on from here is out to the home page.
  const handleFinishedClose = () => { onClose(); navigate('/'); };

  const cardStyle = (selected) => ({
    flex: '1 1 240px',
    minWidth: '220px',
    textAlign: 'left',
    background: selected ? 'rgba(245,166,35,0.08)' : WHITE,
    border: selected ? `2px solid ${AMBER}` : '1px solid rgba(13,27,42,0.15)',
    borderRadius: '10px',
    padding: '16px',
    cursor: 'pointer',
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      backgroundColor: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div style={{
        background: WHITE, borderRadius: '14px', padding: '28px',
        width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto',
      }}>
        {finished ? (
          <>
            <p role="status" style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', color: NAVY, lineHeight: 1.55, marginBottom: '24px', overflowWrap: 'anywhere' }}>
              {finished === 'deactivate' ? t('deleteAccountModal.deactivatedAlert') : t('deleteAccountModal.deletedAlert')}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={handleFinishedClose}
                style={{
                  background: AMBER, border: 'none', color: NAVY,
                  fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700,
                  padding: '10px 22px', borderRadius: '8px', cursor: 'pointer',
                }}
              >
                {t('common.close')}
              </button>
            </div>
          </>
        ) : isSubscription && step === 'reason' ? (
          <>
            <h2 style={{ fontFamily: FONT_HEAD, fontSize: '1.6rem', letterSpacing: '0.04em', color: NAVY, fontWeight: 700, marginBottom: '10px' }}>
              {t('deleteAccountModal.deleteOrDeactivateHeading')}
            </h2>
            <p style={{ fontFamily: FONT_BODY, fontSize: '0.9rem', color: 'rgba(13,27,42,0.65)', marginBottom: '20px', lineHeight: 1.5 }}>
              {t('deleteAccountModal.leavingReasonSubtitle')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '18px' }}>
              {CANCEL_REASONS.map(opt => (
                <label
                  key={opt.value}
                  onClick={() => setReasonOption(opt.value)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                    background: reasonOption === opt.value ? 'rgba(245,166,35,0.08)' : WHITE,
                    border: reasonOption === opt.value ? `2px solid ${AMBER}` : '1px solid rgba(13,27,42,0.15)',
                    borderRadius: '10px', padding: '12px 14px',
                  }}
                >
                  <input type="radio" checked={reasonOption === opt.value} onChange={() => setReasonOption(opt.value)} style={{ cursor: 'pointer' }} />
                  <span style={{ fontFamily: FONT_BODY, fontSize: '0.9rem', color: NAVY }}>{t(opt.labelKey)}</span>
                </label>
              ))}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontFamily: FONT_BODY, fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '6px' }}>
                {reasonOption === 'Other' ? t('deleteAccountModal.tellUsMoreLabel') : t('deleteAccountModal.additionalDetailLabel')}
              </label>
              <textarea
                value={reasonDetail}
                onChange={e => setReasonDetail(e.target.value)}
                placeholder={reasonOption === 'Other' ? t('deleteAccountModal.otherPlaceholder') : t('deleteAccountModal.detailPlaceholder')}
                rows={3}
                style={{
                  width: '100%', padding: '10px 14px', fontSize: '0.9rem',
                  border: '1px solid rgba(13,27,42,0.2)', borderRadius: '8px',
                  outline: 'none', fontFamily: FONT_BODY, color: NAVY, backgroundColor: WHITE, resize: 'vertical',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                style={{
                  background: 'transparent', border: '1px solid rgba(13,27,42,0.25)', color: 'rgba(13,27,42,0.7)',
                  fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 600,
                  padding: '10px 22px', borderRadius: '8px', cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => reasonValid && setStep('confirm')}
                disabled={!reasonValid}
                style={{
                  background: reasonValid ? AMBER : 'rgba(245,166,35,0.4)', border: 'none', color: NAVY,
                  fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700,
                  padding: '10px 22px', borderRadius: '8px', cursor: reasonValid ? 'pointer' : 'not-allowed',
                }}
              >
                {t('deleteAccountModal.continueButton')}
              </button>
            </div>
          </>
        ) : (
        <>
        <h2 style={{ fontFamily: FONT_HEAD, fontSize: '1.6rem', letterSpacing: '0.04em', color: NAVY, fontWeight: 700, marginBottom: '10px' }}>
          {isSubscription ? t('deleteAccountModal.deleteOrDeactivateHeading') : t('deleteAccountModal.deleteAccountHeading')}
        </h2>
        <p style={{ fontFamily: FONT_BODY, fontSize: '0.9rem', color: 'rgba(13,27,42,0.65)', marginBottom: '20px', lineHeight: 1.5 }}>
          {t('deleteAccountModal.confirmSubtitle')}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
          <div onClick={() => chooseOption('deactivate')} style={cardStyle(option === 'deactivate')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <input type="radio" checked={option === 'deactivate'} onChange={() => chooseOption('deactivate')} style={{ cursor: 'pointer' }} />
              <span style={{ fontFamily: FONT_BODY, fontSize: '0.92rem', fontWeight: 700, color: NAVY }}>{t('deleteAccountModal.deactivateOptionTitle')}</span>
            </div>
            <p style={{ fontFamily: FONT_BODY, fontSize: '0.82rem', color: 'rgba(13,27,42,0.6)', lineHeight: 1.5 }}>
              {t('deleteAccountModal.deactivateOptionBody')}
            </p>
          </div>

          <div onClick={() => chooseOption('delete')} style={cardStyle(option === 'delete')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <input type="radio" checked={option === 'delete'} onChange={() => chooseOption('delete')} style={{ cursor: 'pointer' }} />
              <span style={{ fontFamily: FONT_BODY, fontSize: '0.92rem', fontWeight: 700, color: NAVY }}>{t('deleteAccountModal.deleteOptionTitle')}</span>
            </div>
            <p style={{ fontFamily: FONT_BODY, fontSize: '0.82rem', color: 'rgba(13,27,42,0.6)', lineHeight: 1.5 }}>
              {t('deleteAccountModal.deleteOptionBody')}
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontFamily: FONT_BODY, fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '6px' }}>
            {t('deleteAccountModal.typeToConfirmLabel', { word: 'DELETE' })}
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder="DELETE"
            style={{
              width: '100%', padding: '10px 14px', fontSize: '0.9rem',
              border: '1px solid rgba(13,27,42,0.2)', borderRadius: '8px',
              outline: 'none', fontFamily: FONT_BODY, color: NAVY, backgroundColor: WHITE,
            }}
          />
        </div>

        {errorKind && (
          <div ref={errorRef} style={{ marginBottom: '20px', scrollMarginBottom: '28px' }}>
            <p
              role="alert"
              data-delete-error={errorKind}
              style={{
                fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 600, color: RED, lineHeight: 1.5,
                background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)',
                borderRadius: '8px', padding: '10px 14px', overflowWrap: 'anywhere',
              }}
            >
              {errorKind === 'billing' ? t('deleteAccountModal.billingBlockedError') : t('deleteAccountModal.genericError')}
            </p>
            {errorKind === 'billing' && onContactSupport && (
              <button
                data-contact-support
                onClick={onContactSupport}
                style={{
                  background: NAVY, border: 'none', color: WHITE, marginTop: '12px',
                  fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700,
                  padding: '10px 22px', borderRadius: '8px', cursor: 'pointer',
                }}
              >
                {t('deleteAccountModal.contactSupportButton')}
              </button>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={processing}
            style={{
              background: 'transparent', border: '1px solid rgba(13,27,42,0.25)', color: 'rgba(13,27,42,0.7)',
              fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 600,
              padding: '10px 22px', borderRadius: '8px', cursor: processing ? 'default' : 'pointer',
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            style={{
              background: canConfirm ? RED : 'rgba(220,38,38,0.35)', border: 'none', color: WHITE,
              fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700,
              padding: '10px 22px', borderRadius: '8px', cursor: canConfirm ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {processing ? t('deleteAccountModal.processingButton') : t('deleteAccountModal.confirmButton')}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
